<script>

import PageAlert from '~/components/PageAlert.vue'
import ResetGrid from '~/components/ResetGrid.vue'
import ToolbarSearch from '~/components/ToolbarSearch.vue'
import linkable from '~/mixins/linkable'
import storable from '~/mixins/storable'
import CustomNoRowsOverlay from '~/plugins/ag-grid/components/CustomNoRowsOverlay.vue'
import DataTable from '~/plugins/ag-grid/DataTable.vue'
import tableColumnStateParent from '~/plugins/ag-grid/mixins/table-columns-state-parent'
import exportable from '~/plugins/ag-grid/mixins/table-exportable'
import tableFilterableParent from '~/plugins/ag-grid/mixins/table-filterable-parent'

export default {
  name: 'DataTablePage',

  components: {
    DataTable,
    PageAlert,
    ResetGrid,
    ToolbarSearch
  },

  mixins: [
    tableFilterableParent,
    tableColumnStateParent,
    storable(),
    exportable(),
    linkable('appliedSettings')
  ],

  inject: {
    tableLinkable: {
      default: null
    }
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: undefined
    },
    exportable: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    showType: {
      type: Boolean,
      default: true
    },
    columnDefs: {
      type: Array,
      required: true
    },
    dataSource: {
      type: Object,
      required: true
    },
    /**
     * When true, displays error alert on page with a retry button,
     * components should listen for 'retry-errors' event to handle it.
     */
    hasErrors: {
      type: Boolean,
      default: false
    },
    /**
     * An object that will be persisted by linkable/storable along with
     * the table state and filters. This is useful for persisting state
     * and filters that are not directly related to the table, like a
     * date range used by the api query.
     */
    externalState: {
      type: Object,
      default: () => ({})
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    processCellForClipboard: {
      type: Function,
      default: null
    },
    getContextMenuItems: {
      type: Function,
      default: null
    },
    shouldSetLastScrollPosition: {
      type: Boolean,
      default: false
    },
    rowSelection: {
      type: Object,
      default: null
    }
  },

  data: () => ({
    noRowsOverlayComponent: null,
    noRowsOverlayComponentParams: null,
    gridApi: null
  }),

  computed: {
    // Fallback to undefined due to PageAlert value prop expecting a string
    errorMessage: vm => vm.hasErrors ? 'Results failed to load, please try again' : undefined,
    hasNoFilteredRows: ({ tableFilterable }) => tableFilterable.filteredRowCount === 0,
    hasFilters: ({ tableFilterable }) => Object.keys(tableFilterable.filters).length > 0,
    hasSearchTerm: ({ tableFilterable }) => tableFilterable.searchTerm?.length > 0,
    hasChangedState: ({ tableColumnsState_state: columnState, hasFilters, hasSearchTerm }) =>
      columnState !== '' || hasFilters || hasSearchTerm,
    hasSelectedRows: ({ selectedRows }) => selectedRows.length > 0,
    shouldShowMiddleBar: ({ $slots: { middleBar }, hasChangedState }) => Boolean(middleBar ?? hasChangedState),
    dataTableHeight: vm => `calc(100vh - 2 * 64px${vm.toggleElementsHeight})`,
    toggleElementsHeight: vm => [vm.shouldShowMiddleBar, vm.hasErrors]
      .reduce((string, hasElement) => hasElement ? string + ' - 64px' : string, ''),
    appliedSettings: {
      get: ({ tableLinkable, tableColumnsState_state: tableState, filtersAsArray, externalState }) => {
        if (!tableLinkable) return

        return [tableState, filtersAsArray, externalState]
      },
      set ([tableState, filtersAsArray, externalState]) {
        this.tableColumnsState_state = tableState
        this.filtersAsArray = filtersAsArray
        this.$emit('update:externalState', externalState)
      }
    }
  },

  watch: {
    hasNoFilteredRows: 'updateOverlayVisibility',
    hasChangedState: 'updateOverlayVisibility',
    loading: 'updateOverlayVisibility'
  },

  created () {
    // Set the data source on the component instance so that it
    // can be accessed by the table-filterable-parent mixin
    this.$options.dataSource = this.dataSource

    this.noRowsOverlayComponent = CustomNoRowsOverlay
    this.noRowsOverlayComponentParams = {
      action: () => {
        this.resetGrid()
      }
    }
  },

  async mounted () {
    await this.handleMounted()
  },

  methods: {
    onGridReady (params) {
      this.gridApi = params.api

      this.gridApi.hideOverlay()
    },
    async handleMounted () {
      await nextTick()

      if (!this.tableLinkable?.persistedQuery && this.tableLinkable?.directNavigationQuery) {
        this.appliedSettings = []
        return
      }

      if (this.tableLinkable?.persistedQuery) {
        this.linkable_loadParam(this.tableLinkable.persistedQuery)
      }
    },
    setFilters (filters) {
      this.tableFilterable.filters = filters
    },
    setSearchTerm (searchTerm) {
      this.tableFilterable.searchTerm = searchTerm
    },
    resetGrid () {
      this.gridApi.resetColumnState()
      this.tableFilterable.filters = {}
      this.tableFilterable.searchTerm = ''
    },
    updateOverlayVisibility () {
      if (!this.gridApi) return

      const shouldShowOverlay = this.hasNoFilteredRows && this.hasChangedState && !this.loading
      shouldShowOverlay ? this.gridApi.showNoRowsOverlay() : this.gridApi.hideOverlay()
    }
  }
}
</script>

<template>
  <div class="data-table-page">
    <PageAlert
      :text="errorMessage"
      type="error"
    >
      <template #actions>
        <VBtn
          variant="text"
          :loading="loading"
          @click="$emit('retry-errors')"
        >
          Retry
        </VBtn>
      </template>
    </PageAlert>
    <slot
      v-if="hasSelectedRows"
      name="actionsBar"
    />
    <ToolbarSearch
      v-else
      v-model="tableFilterable.searchTerm"
      :title="title"
      :type="type"
      :total="totalDisplayed"
      :loading="loading"
      :show-type="showType"
    >
      <template #actionItems>
        <slot name="actions" />
        <VTooltip
          v-if="exportable"
          location="bottom"
          bar-icon="$download"
        >
          <template #activator="{ props }">
            <VBtn
              :loading="loading"
              class="mx-1"
              icon
              v-bind="props"
              @click="exportAsCsv"
            >
              <VIcon>$download</VIcon>
            </VBtn>
          </template>
          <span>Download CSV</span>
        </VTooltip>
      </template>
    </ToolbarSearch>
    <slot
      name="middleBar"
      v-bind="{
        filters: tableFilterable.filters,
        hasChangedState,
        setFilters,
        resetGrid,
        searchTerm: tableFilterable.searchTerm,
        setSearchTerm
      }"
    >
      <div
        class="d-flex px-4 flex-row data-table-page__middle-bar align-center justify-end"
        :class="{ 'data-table-page__middle-bar--open': shouldShowMiddleBar }"
      >
        <slot
          name="middleBarContent"
          v-bind="{
            filters: tableFilterable.filters,
            setFilters,
            hasChangedState,
            resetGrid,
            searchTerm: tableFilterable.searchTerm,
            setSearchTerm
          }"
        >
          <ResetGrid
            :loading="loading"
            @reset="resetGrid"
          />
        </slot>
      </div>
    </slot>
    <DataTable
      ref="dataTable"
      :column-defs="columnDefs"
      :data-updated="tableFilterable.rowCount"
      :data="$options.dataSource.allData"
      :get-context-menu-items="getContextMenuItems"
      :height="dataTableHeight"
      :loading="loading"
      :process-cell-for-clipboard="processCellForClipboard"
      :should-set-last-scroll-position="shouldSetLastScrollPosition"
      :row-selection="rowSelection"
      :no-rows-overlay-component="noRowsOverlayComponent"
      :no-rows-overlay-component-params="noRowsOverlayComponentParams"
      v-bind="$attrs"
      @gridReady="onGridReady"
    />
  </div>
</template>
<style lang="scss">
.data-table-page {
  &__middle-bar {
    background-color: #FFF;
    height: 0;
    transition: height 200ms;

    &--open {
      border-bottom: 1px solid rgb(var(--v-theme-grey-lighten-2));
      height: 64px;
    }
  }
}
</style>
